import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import MultiFileUploader from "@/components/shared/MultiFileUploader";
import { serializeFileUrls } from "@/lib/file-utils";

interface Props {
  classes: { id: string; name: string }[];
  nurseryId: string;
  onSuccess: () => void;
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultLessonId?: string;
  defaultClassId?: string;
  defaultTitle?: string;
}

export default function AddAssignmentDialog({ classes, nurseryId, onSuccess, controlledOpen, onOpenChange, defaultLessonId, defaultClassId, defaultTitle }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v: boolean) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { user } = useAuth();
  const { toast } = useToast();

  const [title, setTitle] = useState(defaultTitle || "");
  const [description, setDescription] = useState("");
  const [classId, setClassId] = useState(defaultClassId || "");
  const [lessonId, setLessonId] = useState(defaultLessonId || "");
  const [maxScore, setMaxScore] = useState("10");
  const [dueDate, setDueDate] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const { data: lessons } = useQuery({
    queryKey: ["lessons-for-assignment", nurseryId, classId],
    queryFn: async () => {
      let q = supabase.from("lessons").select("id, title").eq("nursery_id", nurseryId);
      if (classId) q = q.eq("class_id", classId);
      const { data } = await q.order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!nurseryId && open,
  });

  const handleSubmit = async () => {
    if (!title.trim() || !classId || !dueDate || !user) return;
    setLoading(true);
    setUploadProgress(0);

    const uploadedPaths: string[] = [];
    try {
      // Upload all files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split(".").pop();
        const path = `${nurseryId}/assignments/${Date.now()}_${i}.${ext}`;
        const { error: uploadError } = await supabase.storage.from("homework-files").upload(path, file);
        if (uploadError) throw new Error(`خطأ في رفع ${file.name}: ${uploadError.message}`);
        uploadedPaths.push(path);
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      const { error } = await supabase.from("assignments").insert({
        nursery_id: nurseryId,
        title: title.trim(),
        description: description.trim() || null,
        class_id: classId,
        lesson_id: lessonId || null,
        created_by: user.id,
        max_score: parseFloat(maxScore) || 10,
        due_date: new Date(dueDate).toISOString(),
        assignment_type: files.length > 0 ? "file" : "text",
        file_url: serializeFileUrls(uploadedPaths),
      });

      if (error) throw error;

      toast({ title: "✅ تم إنشاء الواجب" });
      setOpen(false);
      resetForm();
      onSuccess();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    }
    setLoading(false);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setClassId("");
    setLessonId("");
    setMaxScore("10");
    setDueDate("");
    setFiles([]);
    setUploadProgress(0);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button>
            <Plus className="w-4 h-4 ml-2" />
            واجب جديد
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>إنشاء واجب جديد</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>عنوان الواجب *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: واجب الأرقام" />
          </div>

          <div className="space-y-2">
            <Label>الوصف</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="تعليمات الواجب..." rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>الفصل *</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>مرتبط بدرس</Label>
              <Select value={lessonId} onValueChange={setLessonId}>
                <SelectTrigger><SelectValue placeholder="اختياري" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">بدون</SelectItem>
                  {(lessons || []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>الدرجة القصوى</Label>
              <Input type="number" min={1} value={maxScore} onChange={(e) => setMaxScore(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>موعد التسليم *</Label>
              <Input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>ملفات مرفقة (حتى 200MB لكل ملف)</Label>
            <MultiFileUploader
              files={files}
              onFilesChange={setFiles}
              compact
            />
          </div>

          {loading && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="space-y-1">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">جاري الرفع... {uploadProgress}%</p>
            </div>
          )}

          <Button className="w-full" onClick={handleSubmit} disabled={loading || !title.trim() || !classId || !dueDate}>
            {loading ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <Plus className="w-4 h-4 ml-2" />}
            إنشاء الواجب
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
